import {
  Languages,
  SaveV2Product,
  V2_PRODUCT_MAX_INGREDIENTS,
  V2ProductMatchLevel as V2ClosestProductLevel,
  V2ProductDto,
  V2ProductOptions,
  V2ProductStatus,
} from '@agroone/entities'

export interface ClosestProduct {
  product: V2ProductDto
  level: V2ClosestProductLevel
  sharedIngredients: number
  isReference: boolean
}

const LEVEL_RANK: Record<V2ClosestProductLevel, number> = { identical: 1, sameIngredients: 2, sharedIngredients: 3 }
const COLLATOR = new Intl.Collator('fr', { numeric: true, sensitivity: 'base' })
const upper = (value: string | null): string => (value ?? '').trim().toUpperCase()

export const ProductRules = {
  isV2Available(environmentName: string | undefined): boolean {
    return environmentName === 'qlf' || environmentName === 'local'
  },

  statusFilter(status: V2ProductStatus): boolean | undefined {
    return status === 'all' ? undefined : status === 'active'
  },

  closestProductLabel(
    closest: Pick<ClosestProduct, 'level' | 'isReference' | 'sharedIngredients'>,
    ingredientCount: number,
  ): { key: string; params: Record<string, number> } {
    if (closest.level === 'identical')
      return { key: closest.isReference ? 'LEVEL_IDENTICAL_REFERENCE' : 'LEVEL_IDENTICAL', params: {} }
    if (closest.level === 'sameIngredients') return { key: 'LEVEL_SAME_INGREDIENTS', params: {} }
    return {
      key: ingredientCount > 1 ? 'LEVEL_SHARED_INGREDIENTS' : 'LEVEL_SHARED_INGREDIENT',
      params: { shared: closest.sharedIngredients, total: ingredientCount },
    }
  },

  normalizeName(name: string): string {
    return name.trim().toUpperCase()
  },

  normalizePesticideType(value: string): string {
    const normalized = value
      .trim()
      .toLowerCase()
      .replace(/cides$/, 'cide')
    return normalized === 'others' ? 'other' : normalized
  },

  pesticideTypes(values: readonly string[]): string[] {
    return [...new Set(values.map((value) => ProductRules.normalizePesticideType(value)))].filter(Boolean)
  },

  /** Distinct manufacturers of a product, in registration order, ignoring case and surrounding spaces. */
  manufacturers(countries: ReadonlyArray<{ manufacturer: string | null }>): string[] {
    const byName = new Map<string, string>()
    for (const { manufacturer } of countries) {
      const name = manufacturer?.trim()
      if (name && !byName.has(name.toUpperCase())) byName.set(name.toUpperCase(), name)
    }
    return [...byName.values()]
  },

  pesticideTypeVariants(value: string): string[] {
    const canonical = ProductRules.normalizePesticideType(value)
    return canonical.endsWith('cide') || canonical === 'other' ? [canonical, `${canonical}s`] : [canonical]
  },

  formulationLabel(
    code: string,
    translations: ReadonlyArray<{ language: string; value: string }>,
    languageName: string | undefined,
  ): string {
    const labelFor = (language: string | undefined): string | undefined =>
      translations
        .find(
          (translation) =>
            translation.language.toLowerCase() === language &&
            translation.value.trim() !== '' &&
            translation.value.trim() !== code,
        )
        ?.value.trim()
    const label = labelFor(languageName?.trim().toLowerCase()) ?? labelFor(Languages['en-us'])
    return label ? `${code} · ${label}` : code
  },

  /**
   * Products sharing at least one ingredient, or linked as reference product in either direction, closest first:
   * identical composition, then same ingredients with other concentrations, then some shared ingredients.
   * Within a level, more shared ingredients, a reference link and an active product come first.
   */
  closestProducts(product: V2ProductDto, candidates: V2ProductDto[]): ClosestProduct[] {
    const mine = new Map(product.ingredients.map((ingredient) => [ingredient.ingredientId, ingredient]))
    const myReferences = new Set(product.countries.map((country) => upper(country.referenceProduct)))
    const myName = upper(product.name)
    const ranked = candidates.flatMap((other) => {
      if (other.id === product.id) return []
      const shared = other.ingredients.filter((ingredient) => mine.has(ingredient.ingredientId))
      const isReference =
        myReferences.has(upper(other.name)) ||
        other.countries.some((country) => upper(country.referenceProduct) === myName)
      if (!shared.length && !isReference) return []
      const sameSet = shared.length === mine.size && mine.size === other.ingredients.length
      const sameConcentration =
        sameSet &&
        shared.every((ingredient) => {
          const own = mine.get(ingredient.ingredientId)
          return (
            own?.concentration === ingredient.concentration &&
            own.concentrationUnitId === ingredient.concentrationUnitId
          )
        })
      const level: V2ClosestProductLevel = sameConcentration
        ? 'identical'
        : sameSet
          ? 'sameIngredients'
          : 'sharedIngredients'
      const score = LEVEL_RANK[level] * 10 - shared.length - (isReference ? 5 : 0) - (other.isActive ? 0.5 : 0)
      return [{ product: other, level, sharedIngredients: shared.length, isReference, score }]
    })
    return ranked
      .sort((left, right) => left.score - right.score || COLLATOR.compare(left.product.name, right.product.name))
      .map(({ score: _score, ...closest }) => closest)
  },

  validationError(product: SaveV2Product, options: V2ProductOptions): string | null {
    if (
      !ProductRules.pesticideTypes(options.pesticideTypes).includes(
        ProductRules.normalizePesticideType(product.pesticideType),
      )
    )
      return 'Invalid pesticide type'
    if (!options.useForms.some((option) => option.id === product.useFormId)) return 'Invalid use form'
    if (!options.formulations.some((option) => option.id === product.formulationId)) return 'Invalid formulation'
    if (product.ingredients.length > V2_PRODUCT_MAX_INGREDIENTS) {
      return `At most ${V2_PRODUCT_MAX_INGREDIENTS} ingredients are allowed`
    }
    if (
      !product.ingredients.length ||
      new Set(product.ingredients.map((row) => row.ingredientId)).size !== product.ingredients.length
    ) {
      return 'At least one ingredient is required; duplicate ingredients are not allowed'
    }
    if (
      !product.countries.length ||
      new Set(product.countries.map((row) => row.countryId)).size !== product.countries.length
    ) {
      return 'At least one country is required; duplicate countries are not allowed'
    }
    if (
      product.ingredients.some(
        (row) =>
          !options.ingredients.some((option) => option.id === row.ingredientId) ||
          !options.concentrationUnits.some((option) => option.id === row.concentrationUnitId),
      )
    )
      return 'Invalid ingredient or concentration unit'
    if (product.countries.some((row) => !options.countries.some((option) => option.id === row.countryId)))
      return 'Invalid country'
    return null
  },
}
